import { type Dispatch } from '@reduxjs/toolkit';
import { AutomationStatus } from '@repo/utils';
import { CaseUtil } from '@vectord/utils';
import nodeService from 'automations/services/nodeService';
import { type RootState } from 'automations/store';
import { swapNodeId } from 'automations/store/reactFlowSlice';
import { AutomationMode } from 'automations/types/automation';
import { type ActionNodeType } from 'automations/types/nodeSchemas/actionNodes';

import { NodeError } from './nodeError';

export const saveActionNode = (nodeId: string, parentNodeId?: string, mode: 'create' | 'edit' = 'create') => async (
  dispatch: Dispatch<any>,
  getState: () => RootState,
) => {
  const { nodes, edges } = getState().automations.flowNodes;
  const { automationData, mode: automationMode } = getState().automations.automationConfig;

  const currentNode = nodes.find((node) => node.id === nodeId) as ActionNodeType;
  if (!currentNode) return null;

  const childNodeId = edges.find((edge) => edge.source === nodeId)?.target;
  const parsedAdditionalOptions = CaseUtil.toCase('snakeCase', currentNode.data.additionalOptions);

  let response;
  if (mode === 'create') {
    if (
      automationData?.status === AutomationStatus.published
      && automationMode !== AutomationMode.liveEdit
    ) throw new NodeError('You are not allowed to save an action node in this state');

    response = await dispatch(nodeService.endpoints.saveActionNode.initiate({
      additionalOptions: parsedAdditionalOptions,
      nodeSubType: currentNode.data.nodeSubType,
      parentNodeId: parentNodeId ?? '',
      nodeName: '',
      executionOrder: currentNode.data?.executionOrder,
      childNodeId,
    }));
  } else {
    if (automationData?.status === AutomationStatus.published && automationMode !== AutomationMode.liveEdit) {
      throw new NodeError('You are not allowed to edit an action node in this state');
    }
    response = await dispatch<any>(nodeService.endpoints.updateNode.initiate({
      id: nodeId,
      additionalOptions: parsedAdditionalOptions,
    }));
  }

  if (response.error) throw response.error;

  if (response.data?.node.id) {
    // update the new node id in store
    dispatch(swapNodeId({ oldNodeId: nodeId, newNodeId: response.data?.node.id }));
  }

  return response.data;
};
